// BulkWhatsAppModal.jsx — Part C (البنود 10-13) · TEMPLATES-ONLY
//
// Bulk-send to a list of customers using ONLY an approved Meta template
// picked from the unified صندوق القوالب (via <TemplatePicker/>). Every
// free-text send path has been removed by owner mandate (البند 12/3a):
//   • no wa.me window-open sending
//   • no /ai/generate-message free-text drafting
//   • no manually-typed textarea
// The recipient's name is injected per-recipient into {{1}} by the backend
// at send time (البند 11). Sending goes exclusively through Meta Cloud API:
//   POST /customers/whatsapp-bulk            (now)
//   POST /customers/whatsapp-bulk/schedule   (later)
//   POST /customers/whatsapp-bulk/smart-schedule (smart)
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "./lib/api";
import TemplatePicker from "./TemplatePicker";
import CreateTemplateModal from "./CreateTemplateModal";
import ImagePicker from "./ImagePicker";
import { humanizeSendError, isSendable } from "./lib/bulkWaHelpers";
import WATestSendButton from "./WATestSendButton";
import { DemographicFilters } from "./components/DemographicFilters";
import { AudienceFormula } from "./components/AudienceFormula";
import { zVar } from "./lib/zIndex";
import { useCampaignSendsLock, CAMPAIGN_SENDS_LOCKED_MSG } from "./hooks/useCampaignSendsLock";

// Display metadata ONLY (label + color) — no message text lives here (البند 2a).
const CATEGORY_META = {
  expected: { label: "متوقعون اليوم", color: "#D97706" },
  overdue: { label: "فاتهم موعدهم (استرجاع)", color: "#DC2626" },
  arrived: { label: "شكر (وصلوا اليوم)", color: "#16A34A" },
};

// البند 11 — default Meta template for the «فاتهم موعدهم» section.
const DEFAULT_TEMPLATE = {
  overdue: "thawany_expected_visitors_late_v1",
};

export default function BulkWhatsAppModal({ customers, category = "expected", onClose, manualCustomers = null, reliability = null }) {
  // جولة تصحيح 2026-10-14 (المرحلة 1) / البند 12 — مفتاح إيقاف الإرسال الجماعي
  const { locked: sendsLocked } = useCampaignSendsLock();
  const cat = CATEGORY_META[category] || CATEGORY_META.expected;
  const [picked, setPicked] = useState(null); // TemplatePicker onChange payload
  // PROD-FIX-2 (بند 2) — زر «إنشاء قالب جديد» كان معطَّلاً هنا لعدم تمرير
  // onCreateNew؛ نفس مسار الإنشاء العامل في صفحة الاستبيانات (CreateTemplateModal).
  const [showCreateTpl, setShowCreateTpl] = useState(false);
  const [tplRefreshKey, setTplRefreshKey] = useState(0);
  const [headerImageId, setHeaderImageId] = useState(null); // (رأس صورة) media_id
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  const [metaOn, setMetaOn] = useState(null); // null=checking, true/false
  const [sendReport, setSendReport] = useState(null);
  // البند 12 — timing only (now/later/smart). No send-mode selector: Meta only.
  const [timing, setTiming] = useState("now");
  const [sendAt, setSendAt] = useState("");
  // (ج) البند 10 + (د) البند 9 — حالة السقف اليومي + طلب الموافقة
  const [capStatus, setCapStatus] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  // البند 5 — الدرجات المشمولة تُحدَّد صراحةً (لا إرسال أعمى). الافتراضي: عالية فقط.
  const [includedGrades, setIncludedGrades] = useState({ "عالية": true, "متوسطة": false, "منخفضة": false });
  // v-minors 2026-06 — فلاتر ديموغرافية اختيارية (تقاطع على جمهور المتوقَّعين).
  const [demo, setDemo] = useState({ gender: "", age_min: null, age_max: null, include_unknown: false });
  const patchDemo = (p) => setDemo((d) => ({ ...d, ...p }));
  const [demoKept, setDemoKept] = useState(null); // {total, kept, demographics}
  // (ب) البند 7 — وضعان صريحان لا يختلطان: «حسب الدرجة» أو «المحددون يدويًا».
  const hasManual = Array.isArray(manualCustomers) && manualCustomers.length > 0;
  const [mode, setMode] = useState(hasManual ? "manual" : "grade");
  const gradeOf = (c) => c.confidence || "منخفضة";
  const RELIABILITY = reliability || {};
  // المستلمون النهائيون حسب الوضع المختار وحده (لا خلط — البند 7).
  const visible = mode === "manual"
    ? (manualCustomers || [])
    : customers.filter((c) => includedGrades[gradeOf(c)]);

  // Detect Meta configuration on mount
  useEffect(() => {
    let live = true;
    api.get("/customers/whatsapp-status")
      .then((r) => { if (live) setMetaOn(!!r.data?.configured); })
      .catch(() => { if (live) setMetaOn(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    setSelectedIdx(new Set(visible.map((_, i) => i)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includedGrades, mode]);

  const gradeCounts = { "عالية": 0, "متوسطة": 0, "منخفضة": 0 };
  customers.forEach((c) => { gradeCounts[gradeOf(c)] = (gradeCounts[gradeOf(c)] || 0) + 1; });

  const toggleAll = () => {
    if (selectedIdx.size === visible.length) setSelectedIdx(new Set());
    else setSelectedIdx(new Set(visible.map((_, i) => i)));
  };
  const toggleOne = (i) => {
    const s = new Set(selectedIdx);
    if (s.has(i)) s.delete(i); else s.add(i);
    setSelectedIdx(s);
  };

  const templateName = picked?.name || "";
  // البند 11 — every required var is filled with the recipient's name ({{1}}=name).
  const bodyParamsKeys = (picked?.vars_required || []).map(() => "name");
  const isImageTpl = picked?.header_format === "IMAGE";
  const imageReady = !isImageTpl || !!headerImageId; // قالب مصوَّر يلزمه صورة
  const canSend = isSendable({ templateName, isReady: picked?.is_ready, selectedCount: selectedIdx.size, metaOn }) && imageReady;

  const buildRecipients = () => visible
    .filter((_, i) => selectedIdx.has(i))
    .map((c) => ({
      phone: c.phone,
      name: c.name || "",
      delta_days: c.delta_days,
      predicted_weekday: c.predicted_weekday,
      top_hour_label: c.top_hour_label,
    }));

  // (ج) البند 10 — حالة السقف قبل الضغط، تتحدّث مع عدد المستلمين المختار
  useEffect(() => {
    let live = true;
    const batch = selectedIdx.size;
    if (batch <= 0) { setCapStatus(null); return; }
    api.get(`/send-caps/my-status?batch=${batch}`)
      .then((r) => { if (live) { setCapStatus(r.data); setRequested(false); } })
      .catch(() => { if (live) setCapStatus(null); });
    return () => { live = false; };
  }, [selectedIdx, mode]);

  const capBlocks = !!capStatus && capStatus.requires_approval === true;

  // v-minors 2026-06 — عدّاد حيّ للتقاطع الديموغرافي على المحددين.
  const demoActive = !!(demo.gender || demo.age_min != null || demo.age_max != null);
  useEffect(() => {
    let live = true;
    const recips = buildRecipients();
    if (!demoActive || recips.length === 0) { setDemoKept(null); return; }
    api.post("/customers/whatsapp-bulk/demographic-preview", { recipients: recips, demographics: demo })
      .then((r) => { if (live) setDemoKept(r.data); })
      .catch(() => { if (live) setDemoKept(null); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, selectedIdx, mode]);

  // (د) البند 9 — طلب موافقة تجاوز السقف (لا يُرسل — يُنشئ طلبًا)
  const requestApproval = async () => {
    setRequesting(true);
    try {
      const endpoint = timing === "smart" ? "smart" : timing === "later" ? "schedule" : "now";
      await api.post("/send-caps/request-approval", {
        endpoint, category,
        template_name: templateName,
        recipients: buildRecipients(),
        demographics: demo,
        header_image_id: headerImageId,
        body_params_keys: bodyParamsKeys,
        language: picked?.language || "ar",
        send_at_ksa: timing === "later" ? sendAt : null,
      });
      setRequested(true);
      toast.success("📩 أُرسل طلب الموافقة لمن يملك صلاحية الاعتماد");
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر إرسال الطلب");
    } finally { setRequesting(false); }
  };

  const sendNow = async () => {
    setSending(true); setSentCount(0); setSendReport(null);
    try {
      const r = await api.post("/customers/whatsapp-bulk", {
        template_name: templateName,
        body_params_keys: bodyParamsKeys,
        language: picked?.language || "ar",
        category,
        recipients: buildRecipients(),
        demographics: demo,
        header_image_id: headerImageId,
      });
      setSendReport(r.data);
      setSentCount(r.data?.sent || 0);
      if (r.data?.sent > 0) {
        toast.success(`تم إرسال ${r.data.sent} رسالة WhatsApp بنجاح`, {
          description: r.data.failed > 0 ? `فشل ${r.data.failed} رسالة — تحقق من التفاصيل` : undefined,
        });
      } else {
        toast.error("لم تُرسل أي رسالة", {
          description: humanizeSendError(r.data?.reason) || "تحقق من اعتماد القالب ونافذة الـ 24 ساعة",
        });
      }
    } catch (e) {
      const err = humanizeSendError(e.response?.data?.detail || e.message);
      setSendReport({ ok: false, error: err });
      toast.error("تعذّر الإرسال", { description: err });
    } finally {
      setSending(false);
    }
  };

  const scheduleSend = async () => {
    if (!sendAt) { toast.error("حدد تاريخ ووقت الإرسال أولاً"); return; }
    setSending(true);
    try {
      const r = await api.post("/customers/whatsapp-bulk/schedule", {
        template_name: templateName,
        body_params_keys: bodyParamsKeys,
        language: picked?.language || "ar",
        category,
        recipients: buildRecipients(),
        demographics: demo,
        header_image_id: headerImageId,
        send_at_ksa: sendAt,
      });
      toast.success(`⏰ تمت جدولة ${r.data.count} رسالة`, {
        description: `سترسل تلقائياً في ${sendAt.replace("T", " الساعة ")} بتوقيت السعودية`,
      });
      window.dispatchEvent(new CustomEvent("tw-bulk-wa-scheduled"));
      onClose();
    } catch (e) {
      toast.error("تعذّرت الجدولة", { description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  const smartSchedule = async () => {
    setSending(true);
    try {
      const r = await api.post("/customers/whatsapp-bulk/smart-schedule", {
        template_name: templateName,
        body_params_keys: bodyParamsKeys,
        language: picked?.language || "ar",
        category,
        recipients: buildRecipients(),
        demographics: demo,
        header_image_id: headerImageId,
      });
      toast.success(`✨ جُدولت ${r.data.total} رسالة على ${r.data.groups} وقت مختلف`, {
        description: (r.data.schedule || []).slice(0, 4)
          .map((s) => `${s.send_at_ksa.split("T")[1]} → ${s.count} عميل`).join(" · "),
      });
      window.dispatchEvent(new CustomEvent("tw-bulk-wa-scheduled"));
      onClose();
    } catch (e) {
      toast.error("تعذّرت الجدولة الذكية", { description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  const doSend = () => (timing === "smart" ? smartSchedule() : timing === "later" ? scheduleSend() : sendNow());

  return (
    <div className="fixed inset-0 z-[var(--z-dropdown)] bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="bulk-wa-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} dir="rtl">
        <div className="p-4 border-b flex justify-between items-start"
             style={{ borderColor: "#eadfc8", background: `linear-gradient(135deg,${cat.color}22 0%,#fff 100%)` }}>
          <div>
            <div className="text-xs opacity-70 mb-1">📩 إرسال بالجملة عبر قالب Meta معتمد</div>
            <h2 className="text-lg font-bold" style={{ color: cat.color }}>{cat.label}</h2>
            <div className="text-[11px] opacity-70 mt-1">
              {visible.length} عميل مشمول · محدد {selectedIdx.size}
            </div>
          </div>
          <button onClick={onClose}
                  className="rounded-full w-8 h-8 hover:bg-gray-100 flex items-center justify-center"
                  data-testid="close-bulk-wa">✕</button>
        </div>

        <div className="p-4 space-y-4">
          {/* البند 3 — تصريح صريح بالقسم الذي يُرسَل منه */}
          <div data-testid="bulk-category-banner" className="rounded-lg p-2 text-xs font-bold"
               style={{ background: `${cat.color}15`, border: `1px solid ${cat.color}55`, color: cat.color }}>
            📤 إرسال لقسم: {cat.label}
          </div>

          {/* v-minors 2026-06 — فلاتر ديموغرافية + صيغة التقاطع + عدّاد حيّ */}
          <div>
            <AudienceFormula
              testid="bulk-demo-formula"
              segmentLabel={`متوقَّع اليوم (${cat.label})`}
              demo={demo}
              count={demoActive && demoKept ? demoKept.kept : selectedIdx.size} />
            {demoActive && demoKept && (
              <div data-testid="bulk-demo-note" className="text-xs mb-2" style={{ color: "#5B21B6" }}>
                من {demoKept.total} محدَّد → {demoKept.kept} بعد الفلتر
                {demoKept.demographics?.minors_in_pool ? ` · منهم ${demoKept.demographics.minors_in_pool} دون 18` : ""}
              </div>
            )}
            <DemographicFilters demo={demo} setDemo={patchDemo} demoInfo={demoKept?.demographics} />
          </div>

          {/* البند 10/11 — approved-template picker; {{1}} = customer name per recipient */}
          <TemplatePicker
            key={tplRefreshKey}
            value={{ name: DEFAULT_TEMPLATE[category] || "" }}
            onChange={setPicked}
            onlyApproved
            exactBodyVars={1}
            blockReasonFn={(vc) => ` — 🚫 هذا القالب يتطلب ${vc} متغيرات، والشاشة توفّر اسم العميل فقط`}
            varsAutoFilled
            autoFillLabelFn={() => "«اسم العميل»"}
            autoFillNote="🔗 المتغيّر {{1}} = اسم العميل، يُدرَج تلقائيًا لكل مستلم عند الإرسال — لا نص حرّ. القوالب متعددة المتغيّرات معطّلة هنا لأن الشاشة توفّر اسم العميل فقط."
            testidPrefix="bulk-tpl"
            onCreateNew={() => setShowCreateTpl(true)}
          />
          {showCreateTpl && (
            <CreateTemplateModal
              onClose={() => setShowCreateTpl(false)}
              onSubmitted={() => setTplRefreshKey((k) => k + 1)}
            />
          )}

          {isImageTpl && (
            <div style={{ marginTop: 10 }}>
              <ImagePicker value={headerImageId} onChange={setHeaderImageId} testidPrefix="bulk-image" />
            </div>
          )}

          {/* البند 1 — منع الإرسال بلا قالب مختار: رسالة عربية واضحة */}
          {metaOn !== null && !templateName && (
            <div data-testid="no-template-warning" className="rounded-lg p-2.5 text-xs font-bold"
                 style={{ background: "#fef3c7", border: "1px solid #f59e0b", color: "#92400e" }}>
              ⚠️ اختر قالبًا معتمدًا قبل الإرسال — لن يُرسَل بلا قالب.
            </div>
          )}

          {/* (ب) البند 7 — مبدّل الوضع الصريح: حسب الدرجة / المحددون يدويًا */}
          {hasManual && (
            <div data-testid="bulk-mode-toggle" style={{ display: "flex", gap: 6, padding: "6px 8px", background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: 10 }}>
              <span className="text-xs font-bold" style={{ color: "#334155", alignSelf: "center" }}>وضع المستلمين:</span>
              {[
                { id: "manual", label: `المحددون يدويًا (${(manualCustomers || []).length})` },
                { id: "grade", label: "حسب الدرجة" },
              ].map((o) => (
                <button key={o.id} type="button" data-testid={`bulk-mode-${o.id}`}
                  onClick={() => setMode(o.id)}
                  className="text-xs font-semibold"
                  style={{ padding: "4px 12px", borderRadius: 999, cursor: "pointer",
                    background: mode === o.id ? "#334155" : "#fff", color: mode === o.id ? "#fff" : "#475569",
                    border: `1px solid ${mode === o.id ? "#334155" : "#cbd5e1"}` }}>
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {/* البند 5 + (أ) البنود 2/3 — اختيار الدرجات (وضع «حسب الدرجة» فقط) */}
          {mode === "grade" && (
          <div data-testid="bulk-grade-picker" style={{ padding: "8px 10px", background: "#faf6ee", border: "1px solid #eadfc8", borderRadius: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span className="text-xs font-bold" style={{ color: "#5c3a1e" }}>🎯 الدرجات المشمولة:</span>
              {["عالية", "متوسطة", "منخفضة"].map((g) => {
                const on = !!includedGrades[g];
                const cnt = gradeCounts[g] || 0;
                const disabled = cnt === 0; // (أ) البند 2 — درجة عددها صفر: باهتة/معطّلة
                return (
                  <button key={g} type="button" data-testid={`bulk-grade-${g}`}
                    onClick={() => !disabled && setIncludedGrades((s) => ({ ...s, [g]: !s[g] }))}
                    disabled={disabled}
                    className="text-xs font-semibold"
                    style={{ padding: "4px 10px", borderRadius: 999, cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.4 : 1,
                      background: on ? "#8b5a2b" : "#fff", color: on ? "#fff" : "#6b5535",
                      border: `1px solid ${on ? "#8b5a2b" : "#eadfc8"}` }}>
                    {on ? "☑" : "☐"} {g} ({cnt})
                  </button>
                );
              })}
            </div>
            {/* (أ) البند 3 — سطر موثوقية كل درجة (٪ من tenant_settings) */}
            <div data-testid="grade-reliability-line" className="text-[10.5px] mt-1.5" style={{ color: "#8a6d3b" }}>
              📊 الموثوقية المقيسة:{" "}
              {["عالية", "متوسطة", "منخفضة"].map((g, i) => (
                <span key={g} data-testid={`reliability-${g}`}>
                  {i > 0 && " · "}{g} {RELIABILITY[g] != null ? `${RELIABILITY[g]}%` : "—"}
                </span>
              ))}
            </div>
          </div>
          )}

          {/* (ب) البند 8 — ملخص قبل الإرسال في الوضع اليدوي + تحذير الدرجة المنخفضة */}
          {mode === "manual" && (
            <div data-testid="manual-summary" style={{ padding: "8px 10px", background: "#eef6ff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
              <div className="text-xs font-bold mb-1" style={{ color: "#1e40af" }}>
                🖐️ محددون يدويًا من الجدول: {(manualCustomers || []).length} عميل
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                {["عالية", "متوسطة", "منخفضة"].map((g) => {
                  const n = (manualCustomers || []).filter((c) => gradeOf(c) === g).length;
                  if (!n) return null;
                  return <span key={g} data-testid={`manual-grade-count-${g}`} className="text-[10.5px] font-semibold" style={{ padding: "2px 8px", borderRadius: 999, background: "#fff", border: "1px solid #cbd5e1", color: "#334155" }}>{g}: {n}</span>;
                })}
              </div>
              {(() => {
                const low = (manualCustomers || []).filter((c) => gradeOf(c) === "منخفضة").length;
                if (!low) return null;
                const pct = RELIABILITY["منخفضة"] != null ? `${RELIABILITY["منخفضة"]}%` : "—";
                return (
                  <div data-testid="manual-low-warning" className="text-[11px] font-bold" style={{ color: "#b91c1c", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "5px 8px" }}>
                    ⚠️ اخترت {low} عميلًا بدرجة منخفضة (موثوقية {pct}).
                  </div>
                );
              })()}
            </div>
          )}

          {/* Customer selection */}
          <div className="rounded-lg border max-h-52 overflow-y-auto" style={{ borderColor: "#eadfc8" }}>
            <div className="sticky top-0 bg-gray-50 border-b px-3 py-2 flex justify-between items-center flex-wrap gap-2"
                 style={{ borderColor: "#eadfc8" }}>
              <div className="flex items-center gap-1 flex-wrap">
                <button onClick={toggleAll}
                        className="text-xs font-semibold hover:underline"
                        style={{ color: "#5c3a1e" }}
                        data-testid="bulk-wa-toggle-all">
                  {selectedIdx.size === visible.length && visible.length > 0 ? "☑ إلغاء الكل" : "☐ الكل"}
                </button>
                {[
                  { n: 20, label: "أول 20" },
                  { n: 30, label: "أول 30" },
                  { n: 40, label: "أول 40" },
                  { n: visible.length, label: "الكل" },
                ].map((opt) => (
                  <button key={opt.n}
                          onClick={() => setSelectedIdx(new Set(visible.slice(0, opt.n).map((_, i) => i)))}
                          data-testid={`bulk-wa-seg-${opt.n}`}
                          className="text-[10px] px-2 py-1 rounded transition-all"
                          style={{
                            background: selectedIdx.size === opt.n ? "#8b5a2b" : "#faf3e6",
                            color: selectedIdx.size === opt.n ? "#fff" : "#5c3a1e",
                            border: "1px solid #eadfc8",
                          }}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="text-[10px] opacity-60">{selectedIdx.size} من {visible.length}</div>
            </div>
            {visible.map((c, i) => (
              <label key={i} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-0 text-sm cursor-pointer hover:bg-amber-50"
                     style={{ borderColor: "#f5ecdd" }}>
                <input type="checkbox"
                       checked={selectedIdx.has(i)}
                       onChange={() => toggleOne(i)}
                       data-testid={`bulk-wa-check-${i}`} />
                <span className="flex-1 truncate font-medium">{c.name || "—"}</span>
                <span className="text-[11px] opacity-60 font-mono" dir="ltr">{c.phone}</span>
              </label>
            ))}
          </div>

          {/* Timing selector: now vs scheduled (iOS segmented) */}
          <div className="rounded-lg p-2 flex items-center gap-2 text-xs flex-wrap"
               style={{ background: "#f8f5ee", border: "1px solid #eadfc8" }}>
            <span className="font-semibold" style={{ color: "#5c3a1e" }}>⏱ التوقيت:</span>
            <div className="flex rounded-full p-0.5" style={{ background: "#eadfc8" }}>
              {[{ id: "now", label: "🚀 الآن" }, { id: "later", label: "⏰ جدولة" }, { id: "smart", label: "✨ ذكية" }].map((t) => (
                <button key={t.id} type="button" onClick={() => setTiming(t.id)}
                        data-testid={`wa-timing-${t.id}`}
                        className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                        style={{
                          background: timing === t.id ? "#fff" : "transparent",
                          color: "#5c3a1e",
                          boxShadow: timing === t.id ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                        }}>{t.label}</button>
              ))}
            </div>
            {timing === "later" && (
              <>
                <input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
                       data-testid="wa-schedule-datetime"
                       className="border rounded-lg px-2 py-1 text-xs"
                       style={{ borderColor: "#d4a574" }} />
                <span className="text-[10px] opacity-60">بتوقيت السعودية 🇸🇦 · يُرسل تلقائياً من الخادم</span>
              </>
            )}
            {timing === "smart" && (
              <span className="text-[10px] font-semibold" data-testid="wa-smart-hint"
                    style={{ color: "#8b5a2b" }}>
                ✨ كل عميل يستلم رسالته قبل ساعة من وقت زيارته المعتاد
              </span>
            )}
            <span className="text-[10px] opacity-60 w-full">
              🔒 حماية التكرار: أي عميل وصلته رسالة لنفس المجموعة اليوم يُتخطّى تلقائياً.
            </span>
          </div>

          {/* Meta status note — sending is Meta-only now */}
          <div className="rounded-lg p-2 text-[11px]" data-testid="wa-meta-note"
               style={{ background: "#fff9ec", border: "1px dashed #d4a574", color: "#5c3a1e" }}>
            {metaOn === null
              ? "⏳ جارٍ فحص اتصال Meta…"
              : metaOn
              ? "🚀 الإرسال حصريًا عبر Meta Cloud API بقالب معتمد (لا نص حرّ)."
              : "⚠️ Meta غير مُفعّل حاليًا — تعذّر الإرسال حتى تُضاف مفاتيح Meta."}
          </div>

          {/* Send report (Meta results) */}
          {sendReport && (
            <div className="rounded-lg p-3 text-xs" style={{
              background: sendReport.ok ? "#dcfce7" : "#fee2e2",
              border: `1px solid ${sendReport.ok ? "#15803d" : "#b91c1c"}`,
              color: sendReport.ok ? "#15803d" : "#b91c1c",
            }} data-testid="wa-send-report">
              {sendReport.ok
                ? <>✅ تم الإرسال: <b>{sendReport.sent}</b> نجاح
                    {sendReport.skipped > 0 && <> · <b>{sendReport.skipped}</b> تخطّي (أُرسلت لهم اليوم)</>}
                    {" "}· <b>{sendReport.failed}</b> فشل من أصل <b>{sendReport.total}</b></>
                : <>❌ فشل الإرسال: {humanizeSendError(sendReport.error || sendReport.reason)}</>}
              {sendReport.ok && sendReport.failed > 0 && (
                <details className="mt-1"><summary className="cursor-pointer">تفاصيل الفشل</summary>
                  <ul className="mt-1 list-disc mr-4">
                    {(sendReport.results || []).filter(x => !x.ok).slice(0, 10).map((r, i) => (
                      <li key={i}>{r.name} ({r.phone}): {r.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* (أ) البند 1 — رسالة صريحة حين لا مستلمين بسبب الدرجات المختارة */}
          {mode === "grade" && visible.length === 0 && (
            <div data-testid="zero-recipients-msg" className="rounded-lg p-3 text-xs font-semibold"
                 style={{ background: "#fef3c7", border: "1px solid #f59e0b", color: "#92400e" }}>
              🚫 لا يوجد عملاء بالدرجات المختارة اليوم
              {" ("}
              {["عالية", "متوسطة", "منخفضة"].filter((g) => includedGrades[g])
                .map((g) => `${g}: ${gradeCounts[g] || 0}`).join("، ") || "لم تُختَر درجة"}
              {"). "}
              {(() => {
                const avail = ["عالية", "متوسطة", "منخفضة"].filter((g) => (gradeCounts[g] || 0) > 0);
                return avail.length
                  ? <>الدرجات المتاحة الآن: <b>{avail.map((g) => `${g} (${gradeCounts[g]})`).join("، ")}</b>.</>
                  : <>لا توجد أي درجات متاحة اليوم.</>;
              })()}
            </div>
          )}

          {/* (ج) البند 10 — حالة السقف اليومي قبل الضغط */}
          {capStatus && selectedIdx.size > 0 && (
            <div data-testid="cap-status-line"
                 className="rounded-lg p-2.5 text-xs font-semibold"
                 style={{ background: capBlocks ? "#fef2f2" : "#f0fdf4",
                          border: `1px solid ${capBlocks ? "#fecaca" : "#bbf7d0"}`,
                          color: capBlocks ? "#b91c1c" : "#166534" }}>
              📊 أرسلتَ اليوم {capStatus.used_today} من أصل {capStatus.cap} · المتبقي {capStatus.remaining}
              {" — "}هذه الدفعة {selectedIdx.size}
              {capBlocks && (
                <div data-testid="cap-exceeded-warning" className="mt-1" style={{ fontWeight: 700 }}>
                  ⚠️ ستتجاوز سقفك اليومي — لن يُرسَل. اطلب موافقة من يملك صلاحية الاعتماد.
                </div>
              )}
            </div>
          )}

          {/* (د) البند 9 — زر «اطلب موافقة» عند تجاوز السقف */}
          {capBlocks && selectedIdx.size > 0 && (
            <div className="flex justify-end pt-1">
              <button type="button" onClick={requestApproval}
                      disabled={requesting || requested || !templateName}
                      data-testid="request-approval-btn"
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                      style={{ background: "#7c3aed" }}>
                {requested ? "✅ أُرسل الطلب — بانتظار الاعتماد" : requesting ? "…جارٍ الطلب" : "🔑 اطلب موافقة"}
              </button>
            </div>
          )}

          {/* Send controls */}
          <div className="flex gap-2 flex-wrap justify-end items-center pt-2 border-t"
               style={{ borderColor: "#eadfc8" }}>
            {sending && (
              <div className="text-xs mr-auto" style={{ color: "#5c3a1e" }}>
                جاري الإرسال… {sentCount} من {selectedIdx.size}
              </div>
            )}
            <button onClick={onClose}
                    className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                    style={{ borderColor: "#d4a574", color: "#5c3a1e" }}
                    data-testid="cancel-bulk-wa"
                    disabled={sending}>إلغاء</button>
            <WATestSendButton
              testid="bulk-wa-test-send"
              disabled={sending || !templateName}
              getPayload={() => ({
                template_name: templateName,
                language: picked?.language || "ar",
                body_params_keys: bodyParamsKeys,
                header_image_id: headerImageId,
                source_label: `الزوار المتوقعين — ${cat.label}`,
              })}
            />
            <button onClick={doSend}
                    disabled={sending || !canSend || capBlocks || sendsLocked || (timing === "later" && !sendAt)}
                    className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90"
                    style={{ background: timing === "smart" ? "#8b5a2b" : timing === "later" ? "#D97706" : "#25D366" }}
                    data-testid="send-bulk-wa">
              {timing === "smart"
                ? `✨ جدولة ذكية — كلٌّ بوقته (${selectedIdx.size})`
                : timing === "later"
                ? `⏰ جدولة الإرسال (${selectedIdx.size})`
                : `🚀 إرسال مباشر عبر Meta (${selectedIdx.size})`}
            </button>
          </div>

          {!canSend && metaOn !== null && (
            <div className="text-[10px] opacity-70 text-center" data-testid="wa-cannot-send-hint">
              {!templateName ? "اختر قالبًا معتمدًا أولاً." : !metaOn ? "Meta غير مُفعّل." : selectedIdx.size === 0 ? "اختر مستلمًا واحدًا على الأقل." : "أكمل متطلبات القالب."}
            </div>
          )}
          {sendsLocked && (
            <div className="text-[11px] text-center font-semibold" style={{ color: "#b91c1c" }} data-testid="bulk-wa-locked-hint">
              {CAMPAIGN_SENDS_LOCKED_MSG}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
